import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import RiraLogo from "../Components/RiraLogo";

const PLANS = [
  {
    id: "starter",
    label: "Starter",
    price: "Free",
    period: "",
    perMonth: null,
    savings: null,
    features: [
      "5 searches per month",
      "Basic rating summary",
      "Star breakdown chart",
      "No credit card required",
    ],
  },
  {
    id: "monthly",
    label: "Pro",
    price: "$9.99",
    period: "/ month",
    perMonth: null,
    savings: "Most popular",
    features: [
      "Unlimited review searches",
      "AI reply drafts (3 tones)",
      "Competitor benchmarking",
      "Keyword trend tracking",
      "Export CSV & PDF",
      "Review request links",
      "Cancel anytime",
    ],
    featured: true,
  },
  {
    id: "yearly",
    label: "Business",
    price: "$24.99",
    period: "/ month",
    perMonth: "$299.88 billed annually",
    savings: "Best value",
    features: [
      "Everything in Pro",
      "Email alerts for bad reviews",
      "Weekly digest emails",
      "Multi-location switching",
      "Response rate tracking",
      "Priority support",
    ],
  },
];

function formatCard(value, prev) {
  const digits = value.replace(/\D/g, "").slice(0, 16);
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(value) {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

function PricingPage() {
  const { user, token, subscribe, logout, isSubscribed } = useAuth();

  useEffect(() => {
    if (isSubscribed) setSuccess(true);
  }, [isSubscribed]);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [success,      setSuccess]      = useState(false);

  const [cardNum,  setCardNum]  = useState("");
  const [expiry,   setExpiry]   = useState("");
  const [cvv,      setCvv]      = useState("");
  const [cardName, setCardName] = useState("");

  const handleChoose = (planId) => { setSelectedPlan(planId); setError(""); };

  const handleChooseStripe = async (planId) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/payments/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: planId }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
    } catch {
      // Stripe not configured — fall through to mock form
    }
    setLoading(false);
    handleChoose(planId);
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!cardName.trim() || cardNum.replace(/\s/g, "").length < 16 || expiry.length < 5 || cvv.length < 3) {
      setError("Please fill in all card details.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await subscribe(selectedPlan);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
    }
    setLoading(false);
  };

  const plan = PLANS.find((p) => p.id === selectedPlan);

  return (
    <div className="pricing-page">
      <header className="pricing-header">
        <div className="pricing-brand">
          <RiraLogo size={30} nameColor="#fff" showTagline taglineColor="#a5b4fc" />
        </div>
        <div className="pricing-header-right">
          <span className="pricing-user-email">{user?.email}</span>
          <button className="pricing-logout-btn" onClick={logout}>Sign out</button>
        </div>
      </header>

      <div className="pricing-body">
        {success ? (
          <div className="pricing-success">
            <div className="pricing-success-icon">🎉</div>
            <h2>You're all set!</h2>
            <p>Your {plan?.label} subscription is now active. Enjoy RIRA!</p>
            <p className="pricing-success-hint">Refreshing dashboard…</p>
          </div>
        ) : !selectedPlan ? (
          <>
            <div className="pricing-hero">
              <h2>Welcome, {user?.name}!</h2>
              <p>Choose a plan to unlock full access to RIRA.</p>
            </div>

            <div className="pricing-cards">
              {PLANS.map((p) => (
                <div key={p.id} className={`pricing-card ${p.featured ? "pricing-card--featured" : ""}`}>
                  {p.savings && <div className="pricing-badge">{p.savings}</div>}
                  <div className="pricing-plan-label">{p.label}</div>
                  <div className="pricing-plan-price">
                    <span className="pricing-amount">{p.price}</span>
                    <span className="pricing-period">{p.period}</span>
                  </div>
                  {p.perMonth && <div className="pricing-per-month">{p.perMonth}</div>}
                  <ul className="pricing-features">
                    {p.features.map((f) => (
                      <li key={f}>
                        <span className="pricing-check">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`pricing-cta ${p.featured ? "pricing-cta--featured" : ""}`}
                    onClick={() => handleChooseStripe(p.id)}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : `Get ${p.label}`}
                  </button>
                </div>
              ))}
            </div>

            <p className="pricing-fine">
              No commitment. Cancel anytime from your account settings.
            </p>
          </>
        ) : (
          <div className="payment-container">
            <div className="payment-card">
              <button className="payment-back" onClick={() => { setSelectedPlan(null); setError(""); }}>
                ← Back to plans
              </button>

              <div className="payment-summary">
                <h3>Complete your subscription</h3>
                <div className="payment-plan-pill">
                  {plan.label} — <strong>{plan.price}{plan.period}</strong>
                </div>
              </div>

              <div className="payment-mock-badge">🔒 Demo mode — no real charge</div>

              <form className="payment-form" onSubmit={handlePay}>
                <div className="payment-field">
                  <label>Name on card</label>
                  <input
                    type="text"
                    placeholder="John Smith"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="payment-field">
                  <label>Card number</label>
                  <input
                    placeholder="4242 4242 4242 4242"
                    value={cardNum}
                    onChange={(e) => setCardNum(formatCard(e.target.value, cardNum))}
                    inputMode="numeric"
                  />
                </div>
                <div className="payment-row">
                  <div className="payment-field">
                    <label>Expiry</label>
                    <input
                      placeholder="MM/YY"
                      value={expiry}
                      onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                      inputMode="numeric"
                      maxLength={5}
                    />
                  </div>
                  <div className="payment-field">
                    <label>CVV</label>
                    <input
                      placeholder="123"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 3))}
                      inputMode="numeric"
                      maxLength={3}
                    />
                  </div>
                </div>

                {error && <div className="payment-error">{error}</div>}

                <button type="submit" className="payment-submit" disabled={loading}>
                  {loading
                    ? <><span className="payment-spinner" /> Processing…</>
                    : `Subscribe — ${plan.price}${plan.period}`}
                </button>
              </form>

              <p className="payment-disclaimer">
                This is a prototype demo. Payment details are not stored or processed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingPage;
